/**
 * Authentication Change Logger
 * 
 * Logs authentication changes (outlet and role changes) with timestamps
 * for audit and debugging purposes.
 */

import type { DeviceRole } from './storage';

export interface AuthChangeLog {
  timestamp: number;
  oldOutletId: string | null;
  newOutletId: string | null;
  oldRole: DeviceRole | null;
  newRole: DeviceRole | null;
  changeType: 'initial_auth' | 'outlet_change' | 'role_change' | 'logout';
  deviceInfo?: {
    userAgent: string;
    platform: string;
  };
}

const AUTH_LOG_KEY = 'photobooth_auth_logs';
const MAX_LOG_ENTRIES = 100; // Keep last 100 entries

/**
 * Log an authentication change event
 */
export function logAuthChange(
  oldOutletId: string | null,
  newOutletId: string | null,
  oldRole: DeviceRole | null,
  newRole: DeviceRole | null,
  changeType: AuthChangeLog['changeType']
): void {
  try {
    const logEntry: AuthChangeLog = {
      timestamp: Date.now(),
      oldOutletId,
      newOutletId,
      oldRole,
      newRole,
      changeType,
      deviceInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
      },
    };

    // Get existing logs
    const logs = getAuthLogs();

    // Add new log entry
    logs.push(logEntry);

    // Keep only the last MAX_LOG_ENTRIES
    const trimmedLogs = logs.slice(-MAX_LOG_ENTRIES);

    // Save to localStorage
    localStorage.setItem(AUTH_LOG_KEY, JSON.stringify(trimmedLogs));

    // Also log to console for debugging
    console.info('[Auth Change]', {
      changeType,
      oldOutlet: oldOutletId,
      newOutlet: newOutletId,
      oldRole,
      newRole,
      timestamp: new Date(logEntry.timestamp).toISOString(),
    });
  } catch (error) {
    console.error('Failed to log authentication change:', error);
    // Non-critical, don't throw
  }
}

/**
 * Get all authentication change logs
 */
export function getAuthLogs(): AuthChangeLog[] {
  try {
    const stored = localStorage.getItem(AUTH_LOG_KEY);
    if (!stored) {
      return [];
    }

    const logs = JSON.parse(stored);
    
    // Validate structure
    if (!Array.isArray(logs)) {
      console.warn('Invalid auth logs format, resetting');
      return [];
    }

    return logs;
  } catch (error) {
    console.error('Failed to load authentication logs:', error);
    return [];
  }
}

/**
 * Clear all authentication logs
 */
export function clearAuthLogs(): void {
  try {
    localStorage.removeItem(AUTH_LOG_KEY);
    console.info('[Auth Logger] Logs cleared');
  } catch (error) {
    console.error('Failed to clear authentication logs:', error);
  }
}

/**
 * Get logs for a specific outlet
 */
export function getLogsForOutlet(outletId: string): AuthChangeLog[] {
  const logs = getAuthLogs();
  return logs.filter(
    log => log.oldOutletId === outletId || log.newOutletId === outletId
  );
}

/**
 * Get recent logs (last N entries)
 */
export function getRecentLogs(count: number = 10): AuthChangeLog[] {
  const logs = getAuthLogs();
  return logs.slice(-count);
}

/**
 * Export logs as JSON string for debugging
 */
export function exportLogs(): string {
  const logs = getAuthLogs();
  return JSON.stringify(logs, null, 2);
}
